import { useEffect, useRef } from "react"
import { agoraVolumeManager } from '../service/AgoraVolumeManager'
import { audioAnalysisManager } from '../service/AudioAnalysisManager'

const bandGains = [1, 3, 4]
const avg = ( arr ) => arr.reduce( ( a, b ) => a + b, 0 ) / arr.length
const normalizeLevel = ( value, noiseFloor ) =>
    value <= noiseFloor ? 0 : value - noiseFloor


// 1. Define constants for better control
const NOISE_FLOOR = 30 // Ignore anything quieter than this (0-255)
const SENSITIVITY = 1.2 // Multiply to make the movement more pronounced

const processValue = ( raw ) => {
    // Subtract noise floor and clamp to 0
    const gated = Math.max( 0, raw - NOISE_FLOOR )
    // Normalize (gated max is now 255 - NOISE_FLOOR)
    return Math.min( 1, ( gated / ( 255 - NOISE_FLOOR ) ) * SENSITIVITY )
}

export const useAudioLevels = ( audioTrack, type = "3barwave", uid ) => {
    const levelsRef = useRef( [0, 0, 0] )
    const rafRef = useRef( null )

    useEffect( () => {
        if ( !audioTrack ) return

        let stopped = false

        const update = () => {
            if ( stopped ) return

            if ( type === "3barwave" ) {
                const { analyser, dataArray } = audioAnalysisManager.getAnalyser( audioTrack )
                if ( analyser && dataArray ) {
                    analyser.getByteFrequencyData( dataArray )
                    levelsRef.current = [
                        processValue( dataArray[Math.floor( dataArray.length * 0.2 )] ),
                        processValue( dataArray[3] ),
                        processValue( dataArray[Math.floor( dataArray.length * 0.5 )] )
                    ]
                }
            } else if ( type === "filled-mic" ) {
                const { dataArray } = audioAnalysisManager.getAnalyser( audioTrack )
                if ( dataArray ) {
                    levelsRef.current = [
                        ( normalizeLevel( avg( dataArray ), 0 ) / 255 ) * 1.5,
                    ]
                }
            }

            rafRef.current = requestAnimationFrame( update )
        }

        const startProcessing = async () => {
            if ( type === "volumebar" ) {
                // Subscription based - doesn't need RAF
                const unsubscribe = agoraVolumeManager.subscribe( uid, ( level ) => {
                    levelsRef.current = [level / 100]
                } )
                return unsubscribe
            }

            // Frequency based - needs RAF
            if ( !audioTrack?.getMediaStreamTrack()?.enabled ) return

            const track = audioTrack.getMediaStreamTrack()
            let tries = 0
            while ( !stopped && track.muted && tries < 100 ) {
                await new Promise( ( res ) => setTimeout( res, 50 ) )
                tries++
            }

            if ( !stopped && !track.muted ) {
                update()
            }
        }

        // Handle the subscription cleanup separately if needed
        let volumeUnsubscribe
        startProcessing().then( unsub => {
            volumeUnsubscribe = unsub
        } )

        return () => {
            stopped = true
            if ( rafRef.current ) cancelAnimationFrame( rafRef.current )
            if ( volumeUnsubscribe ) volumeUnsubscribe()
            // Clean up the manager
            if ( audioTrack ) {
                audioAnalysisManager.removeTrack( audioTrack.getTrackId() )
            }
        }
    }, [audioTrack, uid, type] )

    return { levelsRef }
}
